import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { getSurfaceStore } from '../store/publishedCalendarSurfaceStore';
import type { CalendarSurfaceConfig } from 'owa-calendar-surface-core';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import renderAsAllDay from 'owa-calendar-types/lib/utils/renderAsAllDay';
import {
    createCalendarDayContent,
    createMonthDayCellsDecorator,
    getCalendarMonthContent,
    monthItemTemplate,
    overflowMonthItemTemplate,
    getSurfaceItems,
} from 'owa-calendar-surface';
import {
    createItemsDecorator,
    createMonthItemsDecorator,
    CalendarSurfaceStore,
    LinearItemsLayout,
    DatesColumnProvider,
    inDayItemTemplate,
    allDayItemTemplate,
    isOverflowIndicatorId,
    CalendarSurfaceTransformProps,
} from 'owa-calendar-surface-dategrid';
import { getCharm } from 'owa-calendar-events-store';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';

import {
    calendarItemsEventHandlers,
    monthDayCellsEventHandlers,
    monthOverflowItemsHandlers,
} from './calendarSurfaceEventHandlers';

const overflowItemsDecorator = createItemsDecorator(getSurfaceStore());

function getPublishedCalendarConfig(): CalendarSurfaceConfig {
    const store = getSurfaceStore();
    store.enableDragDrop = false;
    store.dateRangeType = DateRangeType.Month; // anonymous calendar always starts with the month range
    let itemsDecorator = createItemsDecorator(store);
    const inDayItemConfig = {
        itemsEventHandlers: calendarItemsEventHandlers,
        itemsDecorator: itemsDecorator,
        template: (store, model, state) =>
            inDayItemTemplate(
                store,
                model,
                getCalendarEventColorScheme(model),
                getCharm(model),
                state
            ),
        colorProvider: getCalendarEventColorScheme,
    };
    const allDayItemConfig = {
        itemsEventHandlers: calendarItemsEventHandlers,
        itemsDecorator: itemsDecorator,
        template: (store, model, state) =>
            allDayItemTemplate(
                store,
                model,
                getCalendarEventColorScheme(model),
                getCharm(model),
                state
            ),
        colorProvider: getCalendarEventColorScheme,
    };

    let getItemConfig = (model: CalendarEvent) => {
        if (isOverflowIndicatorId(model.ItemId.Id)) {
            return null;
        }

        if (renderAsAllDay(model)) {
            return allDayItemConfig;
        }

        return inDayItemConfig;
    };

    let dayConfig: CalendarSurfaceTransformProps = {
        columnProvider: new DatesColumnProvider(store),
        itemsLayout: new LinearItemsLayout(store),
        disableWorkingHours: true,
        disableColumnSelection: true,
        getItemConfig: getItemConfig,
        supportExpandableAllDayWell: () => false,
        shouldShowCurrentTime: () => true,
    };

    let monthItemsDecorator = createMonthItemsDecorator(store);
    const monthItemsConfig = {
        itemsEventHandlers: calendarItemsEventHandlers,
        itemsDecorator: monthItemsDecorator,
        template: (store, model, state) =>
            monthItemTemplate(
                store,
                model,
                getCalendarEventColorScheme(model),
                getCharm(model),
                state
            ),
        colorProvider: getCalendarEventColorScheme,
    };

    const monthOverflowItemsConfig = {
        itemsEventHandlers: monthOverflowItemsHandlers,
        itemsDecorator: overflowItemsDecorator,
        template: overflowMonthItemTemplate,
    };

    const monthConfig = {
        monthDayCellsConfig: createMonthDayCellConfig(store),
        getItemConfig: event =>
            !isOverflowIndicatorId(event.ItemId.Id) ? monthItemsConfig : monthOverflowItemsConfig,
    };

    return {
        dayViewConfig: {
            calendarDayContentConfigProvider: () => {
                return [
                    {
                        content: createCalendarDayContent(store, dayConfig, () => {
                            return getSurfaceItems(store);
                        }),
                    },
                ];
            },
            minWidthProvider: () => (store.dateRangeType == DateRangeType.Day ? 200 : 500),
            setFocusedElement: () => {},
        },
        monthViewConfig: {
            contentGenerator: () => getCalendarMonthContent(store, monthConfig),
            setFocusedElement: () => {},
        },
    };
}

const publishedCalendarConfig = getPublishedCalendarConfig();
export default publishedCalendarConfig;

function createMonthDayCellConfig(store: CalendarSurfaceStore) {
    return {
        itemsEventHandlers: monthDayCellsEventHandlers,
        itemsDecorator: createMonthDayCellsDecorator(store),
    };
}
